/**
 * CheckInScheduleEditorMolecule.tsx
 *
 * Editor for configuring check-in schedules on manual status factors.
 * Supports:
 * - Existing timeframe selection
 * - Recurring schedule (RRULE)
 * - New timeframe creation
 * - Perspective-based responsibility assignment
 * - Reminder and escalation configuration
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'localization';
import { InputNumber } from 'primereact/inputnumber';
import { Divider } from 'primereact/divider';
import { SelectButton, SelectButtonChangeEvent } from 'primereact/selectbutton';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import SchedulesEditorMolecule from '../SchedulesEditor/SchedulesEditorMolecule';
import PrimeReactSingleSelect, {
  type OptionType,
} from '../../atoms/single-select-dropdown/single-select-dropdown';
import SphereSelectDialogOrganism from '../../organisms/marketplace/SphereSelectDialogOrganism';
import type { SphereSearchResult } from '../../../hooks/useSphereSearch';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useActiveOrganization } from '@center-technologies/shared-api-clients';
import { getScheduleTemplates } from '@center-platform/spheres/identity';
import { RRule } from 'rrule';
import type { FilterExpression } from 'types/filters';
import type { UnitValue } from 'types/schema';
import type { CheckInScheduleTemplate } from 'types/spherePoints';

/**
 * Parses an RRULE string and returns a canonical option subset for semantic
 * comparison. `SchedulesEditorMolecule` re-serializes via `new RRule(...)`,
 * which adds `RRULE:` prefix, an explicit `INTERVAL=1`, and encodes the time
 * as BYHOUR/BYMINUTE/BYSECOND — none of which are present in the DB templates'
 * bare strings. Direct string comparison would always report divergence.
 *
 * Time-of-day fields are excluded on purpose: templates carry `dueTime`
 * separately, so the rrule's BY-time fields aren't part of the schedule's
 * identity for promotion purposes.
 */
function canonicalRrule(rruleString: string | undefined): string {
  if (!rruleString) return '';
  try {
    const cleaned = rruleString.replace(/^RRULE:/i, '').replace(/DTSTART[^;]*;?/gi, '');
    const opts = RRule.parseString(cleaned);
    const parts: string[] = [];
    // rrule.Frequency: 0=YEARLY, 1=MONTHLY, 2=WEEKLY, 3=DAILY, 4=HOURLY, 5=MINUTELY, 6=SECONDLY
    const FREQ_NAMES = ['YEARLY', 'MONTHLY', 'WEEKLY', 'DAILY', 'HOURLY', 'MINUTELY', 'SECONDLY'];
    if (opts.freq !== undefined) parts.push(`FREQ=${FREQ_NAMES[opts.freq] ?? opts.freq}`);
    if (opts.interval && opts.interval !== 1) parts.push(`INTERVAL=${opts.interval}`);
    // rrule types every BY-field as `T | T[] | null`; normalize to an array so
    // sort/spread/length are always valid regardless of the scalar/array form
    // rrule chose to hand back.
    const toArray = <T,>(v: T | T[] | null | undefined): T[] =>
      v == null ? [] : Array.isArray(v) ? v : [v];

    const byweekday = toArray<unknown>(opts.byweekday);
    if (byweekday.length > 0) {
      const days = byweekday
        // rrule normalizes to Weekday instances with a numeric .weekday (0=MO..6=SU),
        // but parseString may hand back raw numbers or strings — coerce to a number.
        .map((d) => {
          if (typeof d === 'number') return d;
          if (d && typeof d === 'object' && 'weekday' in d) return (d as { weekday: number }).weekday;
          return Number(d);
        })
        .sort((a, b) => a - b)
        .join(',');
      parts.push(`BYDAY=${days}`);
    }
    const bymonthday = toArray<number>(opts.bymonthday);
    if (bymonthday.length > 0) {
      parts.push(`BYMONTHDAY=${[...bymonthday].sort((a, b) => a - b).join(',')}`);
    }
    const bymonth = toArray<number>(opts.bymonth);
    if (bymonth.length > 0) {
      parts.push(`BYMONTH=${[...bymonth].sort((a, b) => a - b).join(',')}`);
    }
    const bysetpos = toArray<number>(opts.bysetpos);
    if (bysetpos.length > 0) {
      parts.push(`BYSETPOS=${[...bysetpos].sort((a, b) => a - b).join(',')}`);
    }
    return parts.join(';');
  } catch {
    // Malformed rrule strings fall back to raw comparison so we don't silently
    // report "matches" when in fact we couldn't tell.
    return rruleString;
  }
}
import './CheckInScheduleEditorMolecule.scss';

/** Schema type keys used to filter the shared sphere selector to orbit / timeframe spheres. */
const ORBIT_SPHERE_TYPE = 'center_orbit';
const TIMEFRAME_SPHERE_TYPE = 'center_calendar_timeframe';

// Types
/** @deprecated Use CheckInTriggerType instead */
export type ScheduleSourceType = 'existing_timeframe' | 'recurring' | 'orbit';

/** Check-in trigger type - determines how check-ins are scheduled */
export type CheckInTriggerType = 'adhoc' | 'orbit' | 'timeframe' | 'recurring';

/**
 * Time-unit for the backfill card's "max historical" input.
 * `d` = days, `w` = weeks, `mo` = months, `y` = years.
 * Extracted to a named alias for Sonar (avoid inline union types).
 */
export type MaxHistoricalUnit = 'd' | 'w' | 'mo' | 'y';

/** Due rule for period-based check-ins (orbit or timeframe) */
export type DueRuleType = 'start_of_period' | 'end_of_period' | 'days_before_end' | 'days_after_start' | 'custom';

export type ResponsibilityModeType = 'all_must_check_in' | 'any_one_check_in';
export type AggregationMethodType = 'worst' | 'average' | 'majority';
/**
 * Which submission wins under `any_one_check_in`, or which submission overwrites
 * an earlier one from the same user. See EAINT-747 — this is orthogonal to
 * `aggregationMethod`, not an alternative to it.
 */
export type SubmissionModeType = 'first_wins' | 'latest_wins';

/**
 * Available perspective from schema
 */
export interface AvailablePerspective {
  systemName: string;
  displayName: string;
}

/**
 * Period rule configuration - when within a period the check-in is due
 * Used for orbit-based and timeframe-based check-ins
 */
export interface PeriodRule {
  dueRule: DueRuleType;
  dueRuleDays?: number;        // For days_before_end or days_after_start
  dueRuleRrule?: string;       // For custom (RRULE within period)
  dueTime: string;             // HH:mm format
}

/**
 * Check-in schedule configuration value
 */
export interface CheckInScheduleValue {
  // NEW: Trigger type determines the scheduling approach
  triggerType?: CheckInTriggerType;

  // For 'orbit' trigger:
  orbitId?: string;
  orbitName?: string;

  // For 'timeframe' trigger:
  timeframeId?: string;
  timeframeName?: string;

  // For 'orbit' or 'timeframe' - Period Rule:
  periodRule?: PeriodRule;

  // For 'recurring' trigger (standalone RRULE):
  rrule?: string;
  dtstart?: string;
  timezone?: string;

  // Responsibility configuration (not used for adhoc)
  /**
   * Perspective system name whose assignees are responsible for check-ins.
   * Defaults to 'center_responsible' if it exists on the schema.
   */
  responsiblePerspective?: string;
  responsibilityMode: ResponsibilityModeType;
  aggregationMethod?: AggregationMethodType;
  /**
   * Applies under `any_one_check_in`: does the first submitted status stay, or
   * does the latest submission overwrite. Also governs re-submission by the
   * same responsible user under `all_must_check_in`. Defaults to first_wins.
   */
  submissionMode?: SubmissionModeType;

  // Reminder settings (not used for adhoc)
  reminderHoursBefore: number;
  overdueGracePeriodHours: number;
  escalationIntervalHours: number;
  maxEscalations: number;

  // Late-handling settings (EAINT-886) — how the factor's velocity + status
  // respond when a scheduled check-in is missed. Two independent boolean
  // toggles per Board 3; both can be on. Defaults preserve the pre-EAINT-886
  // behavior: velocity goes stale after 2× the schedule period, status does
  // NOT auto-degrade unless the owner opts in.
  /**
   * When true, if the newest period is more than 2× the schedule period
   * overdue, velocity clamps to `stable`. Un-stales on the next check-in.
   * Default: true (conservative — matches pre-EAINT-886 velocity behavior).
   */
  letVelocityGoStale?: boolean;
  /**
   * When true, the factor's reported status auto-degrades based on hours
   * since the newest overdue period's dueAt:
   *   ≥48h → `needs_attention`
   *   ≥96h → `at_risk`
   * The next successful check-in overwrites `currentStatus`, so no
   * un-degrade path is needed. Default: false.
   */
  degradeStatus?: boolean;

  // Historical periods settings
  /**
   * When true, materializes ALL periods from startDate to now on deployment.
   * Past periods are created with status 'overdue' and generate tasks for users.
   * Default: false (only create future periods)
   */
  requireAllPeriods?: boolean;
  /**
   * Maximum number of days in the past to create periods for when requireAllPeriods is true.
   * Default: 365 days
   */
  maxHistoricalDays?: number;
  /**
   * User's preferred unit for displaying maxHistoricalDays.
   * Default: 'y' (years)
   */
  maxHistoricalUnit?: MaxHistoricalUnit;

  // DEPRECATED: Legacy fields for backwards compatibility
  /** @deprecated Use triggerType instead */
  scheduleSource?: 'existing_timeframe' | 'recurring' | 'orbit';
  /** @deprecated Use periodRule.dueRule instead */
  dueRule?: DueRuleType;
  /** @deprecated Use periodRule.dueRuleDays instead */
  dueRuleDays?: number;
  /** @deprecated Use periodRule.dueRuleRrule instead */
  dueRuleRrule?: string;
  /** @deprecated Use periodRule.dueTime instead */
  dueTime?: string;
  /** @deprecated No longer used */
  periodFilter?: string[];
  /** @deprecated No longer used */
  newTimeframe?: {
    name: string;
    type: 'weekly' | 'monthly' | 'quarterly' | 'custom';
    startDate: string;
    endDate?: string;
  };
}

/**
 * Preset key = a check_in_schedule_templates.system_name from the DB (system or
 * org row), or the client-only sentinel `'custom'` for the ad-hoc RRULE path.
 * `string` because templates are DB-driven — see EAINT-745 for the reconciliation.
 */
export type SchedulePresetKey = string;

export interface CheckInScheduleEditorProps {
  /** Current schedule value */
  value?: Partial<CheckInScheduleValue>;
  /** Callback when schedule changes */
  onChange?: (value: CheckInScheduleValue) => void;
  /** Currently selected schedule template (DB system_name or 'custom') */
  selectedTemplate?: SchedulePresetKey | null;
  /** Callback when template selection changes */
  onTemplateChange?: (template: SchedulePresetKey | null) => void;
  /** Sphere ID context */
  sphereId?: string;
  /** Available perspectives from the schema for responsibility assignment */
  availablePerspectives?: AvailablePerspective[];
  /** Whether the editor is in read-only mode */
  readOnly?: boolean;
  /** Custom class name */
  className?: string;
  // Board 3 WHO CHECKS IN card: comment settings live inside this card when
  // the factor is Scheduled (they render inside STARTING STATUS card for
  // Ad-hoc). These props are optional so non-manual mounts still render.
  /** Current value of the require-comment toggle */
  commentRequired?: boolean;
  /** Callback when the require-comment toggle flips */
  onCommentRequiredChange?: (next: boolean) => void;
  /** Current value of the min-comment-length input */
  minCommentLength?: number;
  /** Callback when the min-comment-length input changes */
  onMinCommentLengthChange?: (next: number) => void;
  /**
   * Board 3 layout: STARTING STATUS renders side-by-side with REMINDERS &
   * ESCALATION. When the parent passes its STARTING STATUS card via this
   * slot, the two cards render in a two-column grid; otherwise REMINDERS
   * renders full-width alone.
   */
  startingStatusSlot?: React.ReactNode;
  /**
   * EAINT-893: DOM element to portal the CADENCE body into. Per the handoff,
   * CADENCE is a top-level card and WHO CHECKS IN / REMINDERS / etc. are
   * SIBLINGS of it — not children. The parent (ManualFactorEditor) wraps its
   * CADENCE `<section>` around an empty slot div and passes the ref here; the
   * CADENCE body (source tiles + orbit/timeframe/recurring picker + Due row)
   * portals into that slot, and the rest of this component's return renders
   * as siblings below.
   *
   * When null/undefined the CADENCE body renders inline in this component's
   * root — preserves any existing caller that doesn't compose a CADENCE
   * section itself.
   */
  cadenceSlot?: HTMLElement | null;
}

/** Default period rule for orbit/timeframe-based check-ins */
export const DEFAULT_PERIOD_RULE: PeriodRule = {
  dueRule: 'end_of_period',
  dueTime: '17:00',
};

const DEFAULT_VALUES: CheckInScheduleValue = {
  triggerType: 'adhoc',
  periodRule: DEFAULT_PERIOD_RULE,
  responsiblePerspective: 'center_responsible',
  responsibilityMode: 'all_must_check_in',
  aggregationMethod: 'worst',
  submissionMode: 'first_wins',
  reminderHoursBefore: 24,
  overdueGracePeriodHours: 4,
  escalationIntervalHours: 24,
  maxEscalations: 3,
  // Late-handling defaults (EAINT-886)
  letVelocityGoStale: true,
  degradeStatus: false,
  // Historical periods defaults
  requireAllPeriods: false,
  maxHistoricalDays: 365,
  maxHistoricalUnit: 'y',
  // Legacy defaults for backwards compatibility
  scheduleSource: 'recurring',
  dueRule: 'end_of_period',
  dueTime: '17:00',
};

/**
 * Field for picking or displaying a reference to another sphere (orbit or timeframe)
 * for a check-in schedule.
 *
 * States:
 * - No value:  "Select an orbit..." / "Select a timeframe..." button opens the dialog
 * - Resolved:  Shows the sphere's name with a "Change" button
 * - Unresolved: The saved id no longer resolves (deleted / inaccessible), or a legacy
 *   name-only value was saved before ids were captured. Renders an explicit
 *   "no longer available" state with a re-pick action so the schedule doesn't
 *   silently point at nothing.
 */
interface SphereReferenceFieldProps {
  /** Persisted sphere id ("" or undefined when nothing has been picked). */
  sphereId?: string;
  /** Cached display name written when the sphere was originally picked. */
  sphereName?: string;
  /** Schema type key used to filter the shared sphere selector. */
  schemaType: string;
  /** i18n key for the button label when nothing has been picked yet. */
  selectPlaceholderKey: string;
  /** i18n key for the dialog title. */
  dialogTitleKey: string;
  /** i18n key for the "no longer available" hint. */
  unresolvedHintKey: string;
  readOnly?: boolean;
  onChange: (sphereId: string, sphereName: string) => void;
}

type ReferenceStatus = 'empty' | 'loading' | 'resolved' | 'unresolved';

const SphereReferenceField: React.FC<SphereReferenceFieldProps> = ({
  sphereId,
  sphereName,
  schemaType,
  selectPlaceholderKey,
  dialogTitleKey,
  unresolvedHintKey,
  readOnly = false,
  onChange,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolvedName, setResolvedName] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<ReferenceStatus>(sphereId ? 'loading' : 'empty');

  // Resolve the current sphereId against the graph so we can distinguish:
  //  - a real, still-accessible sphere (name comes back)
  //  - a broken reference (id present but no name resolves)
  //  - a legacy name-only value (no id at all)
  useEffect(() => {
    let cancelled = false;

    if (!sphereId) {
      // Legacy: name persisted but no id → treat as unresolved so the user re-picks.
      // (Empty across the board means nothing has been chosen yet.)
      setResolvedName(undefined);
      setStatus(sphereName ? 'unresolved' : 'empty');
      return () => {
        cancelled = true;
      };
    }

    if (!graphClient) {
      // No client yet; fall back to the cached name if we have one.
      setResolvedName(sphereName);
      setStatus(sphereName ? 'resolved' : 'loading');
      return () => {
        cancelled = true;
      };
    }

    setStatus('loading');
    globalSphereCache
      .getSphereData(sphereId, graphClient)
      .then((data) => {
        if (cancelled) return;
        const name = data?.info?.sphere?.properties?.name;
        if (name) {
          setResolvedName(name);
          setStatus('resolved');
        } else {
          setResolvedName(undefined);
          setStatus('unresolved');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedName(undefined);
        setStatus('unresolved');
      });

    return () => {
      cancelled = true;
    };
  }, [sphereId, sphereName, graphClient]);

  const openDialog = useCallback(() => {
    if (!readOnly) setDialogOpen(true);
  }, [readOnly]);

  const closeDialog = useCallback(() => setDialogOpen(false), []);

  const handleSelect = useCallback(
    async (sphere: SphereSearchResult): Promise<boolean> => {
      const name = sphere.properties?.name || sphere.label || '';
      onChange(sphere.id, name);
      return true;
    },
    [onChange]
  );

  const fixedContextFilters = useMemo<FilterExpression[]>(
    () => [{ property: 'type', operator: 'equals', value: schemaType }],
    [schemaType]
  );

  const displayName = resolvedName ?? sphereName;

  return (
    <div className="checkin-schedule-editor__reference-field">
      {status === 'empty' && (
        <button
          type="button"
          className="p-inputtext w-full checkin-schedule-editor__reference-picker-btn"
          onClick={openDialog}
          disabled={readOnly}
        >
          <i className="pi pi-search" aria-hidden="true" />
          <span>{t(selectPlaceholderKey)}</span>
        </button>
      )}

      {status === 'loading' && (
        <div className="checkin-schedule-editor__reference-loading">
          <i className="pi pi-spin pi-spinner" aria-hidden="true" />
          <span>{t('common.loading', 'Loading…')}</span>
        </div>
      )}

      {status === 'resolved' && (
        <div className="checkin-schedule-editor__reference-selected">
          <i className="pi pi-check-circle" aria-hidden="true" />
          <span className="checkin-schedule-editor__reference-name">{displayName}</span>
          {!readOnly && (
            <button
              type="button"
              className="checkin-schedule-editor__change-btn"
              onClick={openDialog}
            >
              {t('common.change', 'Change')}
            </button>
          )}
        </div>
      )}

      {status === 'unresolved' && (
        <div className="checkin-schedule-editor__reference-unresolved" role="alert">
          <i className="pi pi-exclamation-triangle" aria-hidden="true" />
          <div className="checkin-schedule-editor__reference-unresolved-body">
            <div className="checkin-schedule-editor__reference-unresolved-title">
              {t('checkInSchedule.unresolvedReference.title', 'No longer available')}
            </div>
            <div className="checkin-schedule-editor__reference-unresolved-detail">
              {t(unresolvedHintKey)}
              {sphereName && (
                <>
                  {' '}
                  <span className="checkin-schedule-editor__reference-unresolved-name">
                    ({sphereName})
                  </span>
                </>
              )}
            </div>
          </div>
          {!readOnly && (
            <button
              type="button"
              className="checkin-schedule-editor__change-btn"
              onClick={openDialog}
            >
              {t('checkInSchedule.unresolvedReference.repick', 'Select another')}
            </button>
          )}
        </div>
      )}

      <SphereSelectDialogOrganism
        open={dialogOpen}
        onClose={closeDialog}
        titleKey={dialogTitleKey}
        onSelect={handleSelect}
        fixedContextFilters={fixedContextFilters}
        showContextSelector={false}
        permission="view"
      />
    </div>
  );
};

const CheckInScheduleEditorMolecule: React.FC<CheckInScheduleEditorProps> = ({
  value,
  onChange,
  selectedTemplate: externalSelectedTemplate,
  onTemplateChange,
  sphereId,
  availablePerspectives = [],
  readOnly = false,
  className = '',
  commentRequired,
  onCommentRequiredChange,
  minCommentLength,
  onMinCommentLengthChange,
  startingStatusSlot,
  cadenceSlot,
}) => {
  const { t, i18n } = useTranslation();
  const apiClientsForEditor = useApiClientsOrNull();

  // Merge provided value with defaults, checking for center_responsible in available perspectives
  const getDefaultPerspective = (): string | undefined => {
    if (value?.responsiblePerspective) return value.responsiblePerspective;
    // Default to center_responsible if available
    const hasCenterResponsible = availablePerspectives.some(p => p.systemName === 'center_responsible');
    return hasCenterResponsible ? 'center_responsible' : availablePerspectives[0]?.systemName;
  };

  const [scheduleValue, setScheduleValue] = useState<CheckInScheduleValue>({
    ...DEFAULT_VALUES,
    ...value,
    responsiblePerspective: getDefaultPerspective(),
  });

  // Internal state for selected template (use external if provided)
  const [internalSelectedTemplate, setInternalSelectedTemplate] = useState<SchedulePresetKey | null>(
    externalSelectedTemplate ?? null
  );
  const selectedTemplate = externalSelectedTemplate ?? internalSelectedTemplate;

  // Update internal state when prop changes
  useEffect(() => {
    if (value) {
      setScheduleValue((prev) => ({ ...prev, ...value }));
    }
  }, [value]);

  // Sync external template changes
  useEffect(() => {
    if (externalSelectedTemplate !== undefined) {
      setInternalSelectedTemplate(externalSelectedTemplate);
    }
  }, [externalSelectedTemplate]);

  // Update default perspective when availablePerspectives changes
  useEffect(() => {
    if (!scheduleValue.responsiblePerspective && availablePerspectives.length > 0) {
      const defaultPerspective = getDefaultPerspective();
      if (defaultPerspective) {
        updateValue({ responsiblePerspective: defaultPerspective });
      }
    }
  }, [availablePerspectives]);

  // Helper to update and emit changes
  const updateValue = (updates: Partial<CheckInScheduleValue>) => {
    const newValue = { ...scheduleValue, ...updates };
    setScheduleValue(newValue);
    onChange?.(newValue);
  };

  // Board 3 Due row — shared between the orbit and timeframe cadence paths.
  // Renders `Due [rule ▾] at [time]`, plus an inline `[days]` input when
  // the rule is `days_before_end`. Extracted so the two paths don't
  // duplicate the render (Sonar duplicated-lines finding).
  const renderDueRow = () => {
    const currentDueRule =
      scheduleValue.periodRule?.dueRule || scheduleValue.dueRule || 'end_of_period';
    const currentDueTime =
      scheduleValue.periodRule?.dueTime || scheduleValue.dueTime || '17:00';
    const currentDueDays =
      scheduleValue.periodRule?.dueRuleDays || scheduleValue.dueRuleDays || 0;
    return (
      <div className="checkin-schedule-editor__due-row">
        <span className="checkin-schedule-editor__due-label">
          {t('checkInSchedule.fields.due', 'Due')}
        </span>
        <select
          className="checkin-schedule-editor__inline-select"
          value={currentDueRule}
          onChange={(e) =>
            updateValue({
              periodRule: {
                ...scheduleValue.periodRule,
                dueRule: e.target.value as DueRuleType,
                dueTime: currentDueTime,
              },
              dueRule: e.target.value as DueRuleType,
            })
          }
          disabled={readOnly}
          aria-label={t('checkInSchedule.fields.dueRule', 'Due rule')}
        >
          {dueRuleOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {currentDueRule === 'days_before_end' && (
          <input
            type="number"
            className="checkin-schedule-editor__due-days"
            min={0}
            max={30}
            value={currentDueDays}
            onChange={(e) =>
              updateValue({
                periodRule: {
                  ...scheduleValue.periodRule,
                  dueRuleDays: Number(e.target.value) || 0,
                  dueRule: currentDueRule,
                  dueTime: currentDueTime,
                },
                dueRuleDays: Number(e.target.value) || 0,
              })
            }
            disabled={readOnly}
            aria-label={t('checkInSchedule.fields.daysBefore', 'Days before end')}
          />
        )}
        <span className="checkin-schedule-editor__due-at">
          {t('checkInSchedule.fields.dueAt', 'at')}
        </span>
        <input
          type="time"
          value={currentDueTime}
          onChange={(e) =>
            updateValue({
              periodRule: {
                ...scheduleValue.periodRule,
                dueTime: e.target.value,
                dueRule: currentDueRule,
              },
              dueTime: e.target.value,
            })
          }
          disabled={readOnly}
          aria-label={t('checkInSchedule.fields.dueTime', 'Due time')}
          className="checkin-schedule-editor__due-time"
        />
      </div>
    );
  };

  // Board 3 tiles: icon + short title + one-line subtitle.
  const triggerTypeOptions: Array<{
    value: CheckInTriggerType;
    label: string;
    subtitle: string;
    icon: string;
  }> = [
    {
      value: 'orbit',
      label: t('checkInSchedule.source.orbit', 'An orbit'),
      subtitle: t(
        'checkInSchedule.source.orbitSubtitle',
        'a recurring rhythm you already run',
      ),
      icon: 'pi pi-sync',
    },
    {
      value: 'timeframe',
      label: t('checkInSchedule.source.timeframe', 'A timeframe'),
      subtitle: t(
        'checkInSchedule.source.timeframeSubtitle',
        "an existing calendar's periods",
      ),
      icon: 'pi pi-calendar',
    },
    {
      value: 'recurring',
      label: t('checkInSchedule.source.recurring', 'Recurring'),
      subtitle: t(
        'checkInSchedule.source.recurringSubtitle',
        'a template or custom schedule',
      ),
      icon: 'pi pi-refresh',
    },
  ];

  // Schedule preset templates — fetched from the schema service so system + org
  // templates and their localizations live in a single source of truth. The
  // 'custom' entry is client-only; it's the ad-hoc RRULE path, not a DB row.
  const [dbTemplates, setDbTemplates] = useState<CheckInScheduleTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const schemaClient = apiClientsForEditor?.schemaClient;
  // Org-owned schedule templates differ per organization, so a switch refetches them.
  const { activeOrganizationId } = useActiveOrganization();

  useEffect(() => {
    if (!schemaClient) return;
    let cancelled = false;
    setTemplatesLoading(true);
    getScheduleTemplates(schemaClient, { locale: i18n.language })
      .then((templates) => {
        if (!cancelled) setDbTemplates(templates);
      })
      .catch((err) => {
        // Drop the previous organization's templates rather than show them under this one.
        console.error('[CheckInScheduleEditor] schedule template fetch failed', err);
        if (!cancelled) setDbTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [schemaClient, activeOrganizationId, i18n.language]);

  // EAINT-846: DB templates only. The client-only `custom` escape hatch is
  // rendered separately below the grid as a full-width dashed tile
  // (see Board 6 mockup). Previously appended here and rendered as just
  // another tile in the same 2-col grid — visually indistinguishable from
  // DB templates.
  const schedulePresets = useMemo(() => {
    return dbTemplates.map((tpl) => ({
      key: tpl.systemName,
      label: tpl.name,
      description: tpl.description,
      icon: tpl.icon,
      category: tpl.category,
      config: tpl.config,
      rrule: tpl.config.rrule ?? '',
      dueTime: tpl.config.dueTime ?? '09:00',
    }));
  }, [dbTemplates]);

  // Look up config + label for the custom sentinel by other paths that
  // still need it (e.g. `selectedTemplateConfig`, `handlePresetSelect`).
  const customPresetConfig = useMemo(
    () => ({
      key: 'custom' as const,
      label: t('checkInSchedule.presets.custom.label', 'Build a custom schedule'),
      description: t('checkInSchedule.presets.custom.description', 'Define your own schedule'),
      icon: 'pi pi-refresh',
      category: 'custom' as never,
      config: {},
      rrule: '',
      dueTime: '09:00',
    }),
    [t],
  );

  // Legacy compatibility - map scheduleSource to triggerType
  const currentTriggerType = useMemo(() => {
    if (scheduleValue.triggerType) return scheduleValue.triggerType;
    // Map legacy scheduleSource to triggerType
    if (scheduleValue.scheduleSource === 'existing_timeframe') return 'timeframe';
    if (scheduleValue.scheduleSource === 'recurring') return 'recurring';
    return 'recurring'; // default
  }, [scheduleValue.triggerType, scheduleValue.scheduleSource]);

  // Due rule options
  const dueRuleOptions = [
    { value: 'start_of_period', label: t('checkInSchedule.dueRule.startOfPeriod', 'at start of period') },
    { value: 'end_of_period', label: t('checkInSchedule.dueRule.endOfPeriod', 'at end of period') },
    { value: 'days_before_end', label: t('checkInSchedule.dueRule.daysBeforeEnd', 'days before end') },
  ];

  // Responsibility mode options
  const responsibilityModeOptions = [
    {
      value: 'all_must_check_in',
      label: t('checkInSchedule.responsibility.allMust', 'All Must Check In'),
    },
    {
      value: 'any_one_check_in',
      label: t('checkInSchedule.responsibility.anyOne', 'Any One Check In'),
    },
  ];

  // Aggregation method options
  const aggregationOptions = [
    { value: 'worst', label: t('checkInSchedule.aggregation.worst', 'Worst Status') },
    { value: 'average', label: t('checkInSchedule.aggregation.average', 'Average') },
    { value: 'majority', label: t('checkInSchedule.aggregation.majority', 'Majority') },
  ];

  // Submission mode options (EAINT-747). Orthogonal to aggregation:
  // aggregation collapses N submitted statuses into one bucket, submission
  // decides which of a user's own submissions counts as "theirs".
  const submissionModeOptions = [
    { value: 'first_wins', label: t('checkInSchedule.submission.firstWins', 'First response wins') },
    { value: 'latest_wins', label: t('checkInSchedule.submission.latestWins', 'Latest response wins') },
  ];

  // Handle trigger type change
  const handleTriggerTypeChange = (e: SelectButtonChangeEvent) => {
    const newType = e.value as CheckInTriggerType;
    updateValue({
      triggerType: newType,
      // Also update legacy field for backwards compatibility
      scheduleSource: newType === 'timeframe' ? 'existing_timeframe' : newType === 'recurring' ? 'recurring' : undefined,
    });
  };

  // Handle timeframe selection
  const handleTimeframeChange = (timeframeId: string, timeframeName?: string) => {
    updateValue({ timeframeId, timeframeName });
  };

  // Handle orbit selection
  const handleOrbitChange = (orbitId: string, orbitName?: string) => {
    updateValue({ orbitId, orbitName });
  };

  // Handle preset template selection
  const handlePresetSelect = (presetKey: SchedulePresetKey) => {
    // Update template state
    setInternalSelectedTemplate(presetKey);
    onTemplateChange?.(presetKey);

    const preset = schedulePresets.find(p => p.key === presetKey);
    if (preset && preset.key !== 'custom') {
      // Apply the FULL template config, not just rrule — the DB templates carry
      // dueTime, responsibilityMode, reminder fields and channels the user is
      // meant to inherit as a starting point. Individual fields remain editable.
      updateValue({
        ...preset.config,
        // Preserve dtstart if the user already had one (they're editing an
        // existing factor); otherwise use today's date as a safe default.
        dtstart: scheduleValue.dtstart || new Date().toISOString().split('T')[0],
      });
    }
    // For 'custom', don't change anything — user configures from scratch.
  };

  // Handle RRULE change from SchedulesEditor
  const handleRruleChange = (rruleValue: { rrule: string; dtstart?: string; timezone?: string }) => {
    updateValue({
      rrule: rruleValue.rrule,
      dtstart: rruleValue.dtstart,
      timezone: rruleValue.timezone,
    });
  };

  /**
   * When a template is selected and the user edits any of its schedule-defining
   * fields, the schedule is "promoted" to custom — fluidly and reversibly per
   * EAINT-745's design correction ("silently promotes"). If the user tweaks a
   * field then tweaks it back, the schedule un-promotes.
   *
   * We compare the currently-applied rrule against what the template would
   * apply. `dtstart` is deliberately excluded — every schedule needs one and
   * templates don't ship one, so a user-set start date isn't a promotion signal.
   */
  const selectedTemplateConfig = useMemo(() => {
    if (!selectedTemplate || selectedTemplate === 'custom') return null;
    return schedulePresets.find((p) => p.key === selectedTemplate) ?? null;
  }, [selectedTemplate, schedulePresets]);

  const isPromotedToCustom = useMemo(() => {
    if (!selectedTemplateConfig) return false;
    return canonicalRrule(scheduleValue.rrule) !== canonicalRrule(selectedTemplateConfig.rrule);
  }, [selectedTemplateConfig, scheduleValue.rrule]);

  // Get the selected perspective display name
  const selectedPerspectiveLabel = useMemo(() => {
    if (!scheduleValue.responsiblePerspective) return null;
    return availablePerspectives.find(p => p.systemName === scheduleValue.responsiblePerspective)?.displayName
      || scheduleValue.responsiblePerspective;
  }, [scheduleValue.responsiblePerspective, availablePerspectives]);

  /*
   * EAINT-927: WHO CHECKS IN picker switched from a native <select> to
   * `PrimeReactSingleSelect`. Its option shape is `{ code, name }` (not
   * `{ value, label }`), so the mapping keys change here. Every option
   * carries every perspective from `availablePerspectives` — that prop
   * is the full sphere-type perspective vocabulary (built via
   * `buildFilterPerspectiveOptions` in the parent), not a filtered
   * subset. Miro flagged "should be the full list" — this always was.
   */
  const perspectiveOptions: OptionType[] = useMemo(() => {
    return availablePerspectives.map(p => ({
      code: p.systemName,
      name: p.displayName,
    }));
  }, [availablePerspectives]);

  // EAINT-887: WHO CHECKS IN "· N people today" count. Read the counts map
  // populated by `POST /spheres/batch-info` via GlobalSphereCache. Undefined
  // (not zero) means "unresolved" — the count segment hides in that case so a
  // stale zero can't flash before the batch call lands.
  const graphClientForCounts = apiClientsForEditor?.graphClient;
  const [perspectiveAssignmentCounts, setPerspectiveAssignmentCounts] = useState<
    Record<string, number> | undefined
  >(undefined);
  useEffect(() => {
    let cancelled = false;
    if (!sphereId || !graphClientForCounts) {
      setPerspectiveAssignmentCounts(undefined);
      return () => {
        cancelled = true;
      };
    }
    globalSphereCache
      .getSphereData(sphereId, graphClientForCounts)
      .then((data) => {
        if (cancelled) return;
        setPerspectiveAssignmentCounts(data?.info?.perspectiveAssignmentCounts);
      })
      .catch(() => {
        if (cancelled) return;
        setPerspectiveAssignmentCounts(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [sphereId, graphClientForCounts]);

  const selectedPerspectiveCount = useMemo(() => {
    const systemName = scheduleValue.responsiblePerspective;
    if (!systemName || !perspectiveAssignmentCounts) return undefined;
    return perspectiveAssignmentCounts[systemName];
  }, [scheduleValue.responsiblePerspective, perspectiveAssignmentCounts]);

  /*
   * EAINT-927: templates for the WHO CHECKS IN perspective picker.
   * Item template renders each perspective in the panel; value template
   * renders the currently-selected perspective in the trigger. Both
   * share the same visual — leading person icon + perspective name.
   */
  const perspectiveItemTemplate = (option: OptionType) => (
    <div className="checkin-schedule-editor__perspective-option">
      <i className="pi pi-user checkin-schedule-editor__perspective-icon" aria-hidden="true" />
      <span>{option.name}</span>
    </div>
  );

  const perspectiveValueTemplate = (option: OptionType | null) => {
    if (!option) {
      return (
        <span className="checkin-schedule-editor__perspective-placeholder">
          {t('checkInSchedule.placeholders.selectPerspective', 'Select a perspective...')}
        </span>
      );
    }
    return (
      <div className="checkin-schedule-editor__perspective-option">
        <i className="pi pi-user checkin-schedule-editor__perspective-icon" aria-hidden="true" />
        <span>{option.name}</span>
      </div>
    );
  };

  /*
   * EAINT-893: The CADENCE body (source tiles + orbit/timeframe/recurring
   * picker + Due row) is extracted into its own subtree so the caller
   * (ManualFactorEditor) can portal it into its CADENCE `<section>` — putting
   * WHO CHECKS IN / REMINDERS / LATE / Advanced / BACKFILL as SIBLINGS of
   * CADENCE per the handoff, not children of it. When `cadenceSlot` is
   * omitted, CADENCE renders inline in this component's root (backwards-
   * compatible with any caller that mounts us in a single-card layout).
   */
  const cadenceBody = (
    <>
      {/* Board 3 CADENCE card body: "Follow the cadence of" label + three
          source-selector tiles (An orbit / A timeframe / Recurring), then
          the source-specific picker beneath. The outer CADENCE card wrapper
          + Ad-hoc/Scheduled segmented live in ManualFactorEditor. */}
      <div className="checkin-schedule-editor__section">
        <label className="checkin-schedule-editor__cadence-label">
          {t('checkInSchedule.sections.followCadenceOf', 'Follow the cadence of')}
        </label>
        <div className="checkin-schedule-editor__source-tiles" role="radiogroup" aria-label={t('checkInSchedule.sections.followCadenceOf', 'Follow the cadence of')}>
          {triggerTypeOptions.map((opt) => {
            const active = currentTriggerType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={`checkin-schedule-editor__source-tile${active ? ' checkin-schedule-editor__source-tile--active' : ''}`}
                disabled={readOnly}
                onClick={() => {
                  if (!active) {
                    handleTriggerTypeChange({ value: opt.value } as SelectButtonChangeEvent);
                  }
                }}
              >
                <i className={`${opt.icon} checkin-schedule-editor__source-tile-icon`} aria-hidden="true" />
                <span className="checkin-schedule-editor__source-tile-label">{opt.label}</span>
                <span className="checkin-schedule-editor__source-tile-subtitle">{opt.subtitle}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Orbit Configuration — title removed per Board 3; the picker is the
          content of the CADENCE card below the source tiles. */}
      {currentTriggerType === 'orbit' && (
        <div className="checkin-schedule-editor__section checkin-schedule-editor__section--source-config">
          <div className="checkin-schedule-editor__field">
            <label>{t('checkInSchedule.fields.orbit', 'Orbit')}</label>
            <SphereReferenceField
              sphereId={scheduleValue.orbitId}
              sphereName={scheduleValue.orbitName}
              schemaType={ORBIT_SPHERE_TYPE}
              selectPlaceholderKey="checkInSchedule.placeholders.selectOrbit"
              dialogTitleKey="checkInSchedule.dialogs.selectOrbit"
              unresolvedHintKey="checkInSchedule.unresolvedReference.orbitDetail"
              readOnly={readOnly}
              onChange={handleOrbitChange}
            />
          </div>

          {renderDueRow()}
        </div>
      )}

      {/* Timeframe Configuration — title removed per Board 3. */}
      {currentTriggerType === 'timeframe' && (
        <div className="checkin-schedule-editor__section checkin-schedule-editor__section--source-config">
          <div className="checkin-schedule-editor__field">
            <label>{t('checkInSchedule.fields.timeframe', 'Timeframe')}</label>
            <SphereReferenceField
              sphereId={scheduleValue.timeframeId}
              sphereName={scheduleValue.timeframeName}
              schemaType={TIMEFRAME_SPHERE_TYPE}
              selectPlaceholderKey="checkInSchedule.placeholders.selectTimeframe"
              dialogTitleKey="checkInSchedule.dialogs.selectTimeframe"
              unresolvedHintKey="checkInSchedule.unresolvedReference.timeframeDetail"
              readOnly={readOnly}
              onChange={handleTimeframeChange}
            />
          </div>

          {renderDueRow()}
        </div>
      )}

      {/* Recurring Schedule Configuration — title removed per Board 3. */}
      {currentTriggerType === 'recurring' && (
        <div className="checkin-schedule-editor__section checkin-schedule-editor__section--source-config">
          {/*
           * Recurring source per EAINT-738's "design corrections" list:
           *
           *   > Selecting a recurring template must flip to the schedule editor
           *   > with fields pre-populated from that template, rather than
           *   > terminating in a static summary card.
           *
           * Which means: eager mount — template gallery (Board 5) when nothing
           * is picked, then flip to the schedule editor (Board 6, currently
           * `SchedulesEditorMolecule`) once a template or Custom is chosen.
           * "Back to templates" returns to the gallery without dropping the
           * schedule value.
           *
           * Note: this file briefly experimented with a compact resource-
           * summary card + inline expansion (EAINT-893 rev.1–7). That's the
           * Board 3 orbit/timeframe pattern, and the epic description
           * explicitly overrides it for the recurring source — the design
           * correction takes precedence over the board mockup.
           */}
          {!selectedTemplate && (
            <div className="checkin-schedule-editor__field">
              <label>{t('checkInSchedule.fields.scheduleTemplate', 'Choose a Schedule')}</label>
              {templatesLoading ? (
                <div className="checkin-schedule-editor__presets-loading">
                  <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                  <span>{t('common.loading', 'Loading...')}</span>
                </div>
              ) : (
                <>
                  <div className="checkin-schedule-editor__presets">
                    {schedulePresets.map((preset) => (
                      <button
                        key={preset.key}
                        type="button"
                        className="checkin-schedule-editor__preset-btn"
                        onClick={() => handlePresetSelect(preset.key as SchedulePresetKey)}
                        disabled={readOnly}
                        title={preset.description}
                      >
                        <i className={`checkin-schedule-editor__preset-icon ${preset.icon}`} />
                        <span className="checkin-schedule-editor__preset-label">{preset.label}</span>
                        <span className="checkin-schedule-editor__preset-desc">{preset.description}</span>
                      </button>
                    ))}
                  </div>
                  {/* EAINT-846: Custom is visually distinct — full-width
                       dashed-border tile below the DB template grid. */}
                  <button
                    type="button"
                    className="checkin-schedule-editor__preset-btn checkin-schedule-editor__preset-btn--custom"
                    onClick={() => handlePresetSelect('custom' as SchedulePresetKey)}
                    disabled={readOnly}
                    data-testid="preset-custom-tile"
                    title={customPresetConfig.description}
                  >
                    <i className={`checkin-schedule-editor__preset-icon ${customPresetConfig.icon}`} />
                    <span className="checkin-schedule-editor__preset-label">
                      {customPresetConfig.label}
                    </span>
                  </button>
                </>
              )}
            </div>
          )}

          {selectedTemplate && (
            <div className="checkin-schedule-editor__field">
              <div className="checkin-schedule-editor__template-status">
                {selectedTemplate === 'custom' ? (
                  <>
                    <i className="pi pi-cog" aria-hidden="true" />
                    <span className="checkin-schedule-editor__template-status-label">
                      {t('checkInSchedule.presets.custom.label', 'Custom')}
                    </span>
                  </>
                ) : isPromotedToCustom ? (
                  <>
                    <i className="pi pi-pencil" aria-hidden="true" />
                    <span className="checkin-schedule-editor__template-status-label">
                      {t(
                        'checkInSchedule.templatePromoted',
                        'Custom (from {{template}})',
                        { template: selectedTemplateConfig?.label ?? selectedTemplate },
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    <i className="pi pi-check-circle" aria-hidden="true" />
                    <span className="checkin-schedule-editor__template-status-label">
                      {selectedTemplateConfig?.label ?? selectedTemplate}
                    </span>
                  </>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    className="checkin-schedule-editor__change-btn"
                    onClick={() => {
                      setInternalSelectedTemplate(null);
                      onTemplateChange?.(null);
                    }}
                  >
                    {t('checkInSchedule.backToTemplates', 'Back to Templates')}
                  </button>
                )}
              </div>
              {selectedTemplateConfig?.description && !isPromotedToCustom && (
                <p className="checkin-schedule-editor__template-status-desc">
                  {selectedTemplateConfig.description}
                </p>
              )}
              <SchedulesEditorMolecule
                value={
                  scheduleValue.rrule
                    ? { rrule: scheduleValue.rrule, dtstart: scheduleValue.dtstart }
                    : undefined
                }
                onScheduleChange={handleRruleChange}
                editable={!readOnly}
                // Promotion to Custom must fire the moment a template's field
                // changes — the "silently promotes" behavior in EAINT-745.
                autoApply
                label={t('checkInSchedule.fields.scheduleEditor', 'Configure Schedule')}
              />
            </div>
          )}

          {renderDueRow()}
        </div>
      )}
    </>
  );

  return (
    <div className={`checkin-schedule-editor ${className}`}>
      {/*
       * EAINT-893: If the caller provided a slot, portal CADENCE into it so
       * ManualFactorEditor's CADENCE `<section>` holds the CADENCE body and
       * WHO CHECKS IN renders as a sibling of that section. Otherwise fall
       * back to rendering CADENCE inline (backwards-compatible).
       */}
      {cadenceSlot
        ? ReactDOM.createPortal(cadenceBody, cadenceSlot)
        : cadenceBody}

      {/* Board 3 WHO CHECKS IN card (was Responsible Users). Wraps
          perspective + completion mode + status aggregation + submission
          mode + Require-comment settings. */}
      <div className="checkin-schedule-editor__card checkin-schedule-editor__card--who-checks-in">
        <div className="checkin-schedule-editor__card-title">
          {t('checkInSchedule.sections.whoChecksIn', 'WHO CHECKS IN')}
        </div>

        {/* Board 3 sentence form: "Whoever is [Responsible ▾] on this sphere
            · N people today". The trailing count only renders once the batch
            info payload has resolved a value for the selected perspective —
            when unresolved (undefined) we hide the segment rather than flash a
            stale zero (EAINT-887). */}
        <div className="checkin-schedule-editor__whoever-row">
          <span className="checkin-schedule-editor__whoever-prefix">
            {t('checkInSchedule.whoChecksIn.whoeverIs', 'Whoever is')}
          </span>
          {/* EAINT-927: shared perspective picker per Miro feedback. Was a
              raw <select>; now the same `PrimeReactSingleSelect` atom used
              across the rest of the editor. `panelClassName` scopes the
              portalled panel styling to this consumer only (opt-in pattern
              introduced in EAINT-893). */}
          <PrimeReactSingleSelect
            value={
              perspectiveOptions.find(
                (o) => o.code === scheduleValue.responsiblePerspective,
              ) ?? null
            }
            options={perspectiveOptions}
            onChange={(e) =>
              updateValue({
                responsiblePerspective: e.value?.code ?? undefined,
              })
            }
            valueTemplate={perspectiveValueTemplate}
            itemTemplate={perspectiveItemTemplate}
            placeholder={t(
              'checkInSchedule.placeholders.selectPerspective',
              'Select a perspective...',
            )}
            className="checkin-schedule-editor__perspective-select"
            panelClassName="checkin-schedule-editor-perspective-panel"
            disabled={readOnly}
            data-testid="whoever-perspective-select"
          />
          <span className="checkin-schedule-editor__whoever-suffix">
            {t('checkInSchedule.whoChecksIn.onThisSphere', 'on this sphere')}
          </span>
          {selectedPerspectiveCount !== undefined && (
            <span
              className="checkin-schedule-editor__whoever-count"
              data-testid="whoever-count"
            >
              {t('checkInSchedule.whoChecksIn.peopleCount', {
                count: selectedPerspectiveCount,
                defaultValue_zero: '· no one today',
                defaultValue_one: '· {{count}} person today',
                defaultValue_other: '· {{count}} people today',
              })}
            </span>
          )}
        </div>

        {availablePerspectives.length === 0 && (
          <div className="checkin-schedule-editor__no-options">
            <i className="pi pi-info-circle" />
            <span>
              {t(
                'checkInSchedule.noPerspectives',
                'No perspectives available. The sphere type must have perspectives defined.'
              )}
            </span>
          </div>
        )}

        {/* Segmented All must check in / Any one, with a conditional inline
            "if they disagree, take [worst status ▾]" that only renders under
            All-must (aggregation is meaningless for Any-one). */}
        <div className="checkin-schedule-editor__mode-row">
          <div className="checkin-schedule-editor__segmented" role="radiogroup" aria-label={t('checkInSchedule.fields.responsibilityMode', 'Completion Mode')}>
            <button
              type="button"
              role="radio"
              aria-checked={scheduleValue.responsibilityMode === 'all_must_check_in'}
              className={`checkin-schedule-editor__segmented-option${scheduleValue.responsibilityMode === 'all_must_check_in' ? ' checkin-schedule-editor__segmented-option--active' : ''}`}
              disabled={readOnly}
              onClick={() => updateValue({ responsibilityMode: 'all_must_check_in' as ResponsibilityModeType })}
            >
              {t('checkInSchedule.whoChecksIn.allMustCheckIn', 'All must check in')}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={scheduleValue.responsibilityMode === 'any_one_check_in'}
              className={`checkin-schedule-editor__segmented-option${scheduleValue.responsibilityMode === 'any_one_check_in' ? ' checkin-schedule-editor__segmented-option--active' : ''}`}
              disabled={readOnly}
              onClick={() => updateValue({ responsibilityMode: 'any_one_check_in' as ResponsibilityModeType })}
            >
              {t('checkInSchedule.whoChecksIn.anyOne', 'Any one')}
            </button>
          </div>
          {scheduleValue.responsibilityMode === 'all_must_check_in' && (
            <>
              <span className="checkin-schedule-editor__disagree-prefix">
                {t('checkInSchedule.whoChecksIn.ifTheyDisagreeTake', '· if they disagree, take')}
              </span>
              <select
                className="checkin-schedule-editor__inline-select"
                value={scheduleValue.aggregationMethod ?? ''}
                onChange={(e) =>
                  updateValue({ aggregationMethod: e.target.value as AggregationMethodType })
                }
                disabled={readOnly}
                aria-label={t('checkInSchedule.fields.aggregation', 'Status Aggregation')}
              >
                {aggregationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label.toLowerCase()}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* Require-comment toggle + inline Min length input.
            Submission Mode is intentionally hidden — it's not in Board 3.
            Follow up with product/design at standup. */}
        {onCommentRequiredChange && (
          <div className="checkin-schedule-editor__comment-block">
            <div className="checkin-schedule-editor__toggle-row">
              <label
                htmlFor="who-checks-in-comment-required"
                className="checkin-schedule-editor__toggle-label"
              >
                {t('statusFactors.manual.fields.commentRequired', 'Require a comment')}
              </label>
              <SwitchAtom
                inputId="who-checks-in-comment-required"
                checked={!!commentRequired}
                onChange={(next) => onCommentRequiredChange(next)}
                disabled={readOnly}
              />
            </div>
            {commentRequired && onMinCommentLengthChange && (
              <div className="checkin-schedule-editor__min-length-row">
                <label htmlFor="who-checks-in-min-comment">
                  {t('statusFactors.manual.fields.minCommentLength', 'Min length')}
                </label>
                <div className="checkin-schedule-editor__min-length-right">
                  <input
                    id="who-checks-in-min-comment"
                    type="number"
                    className="checkin-schedule-editor__min-length-input"
                    value={minCommentLength ?? 0}
                    min={0}
                    max={500}
                    onChange={(e) =>
                      onMinCommentLengthChange(Number(e.target.value) || 0)
                    }
                    disabled={readOnly}
                  />
                  <span className="checkin-schedule-editor__min-length-unit">
                    {t('statusFactors.manual.fields.chars', 'chars')}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Board 3 pairs STARTING STATUS + REMINDERS & ESCALATION in a
          two-column grid. When the parent passes a STARTING STATUS via
          startingStatusSlot we render them side-by-side; without it the
          REMINDERS card takes the full width alone. */}
      <div className={`checkin-schedule-editor__reminders-row${startingStatusSlot ? ' checkin-schedule-editor__reminders-row--paired' : ''}`}>
      {startingStatusSlot}
      <div className="checkin-schedule-editor__card checkin-schedule-editor__card--reminders">
        <div className="checkin-schedule-editor__card-title">
          {t('checkInSchedule.sections.reminders', 'REMINDERS & ESCALATION')}
        </div>

        {/* Board 3: four rows, each [label] ..... [input] [unit].
            Raw <input type=number> — no PrimeReact InputNumber wrapper. */}
        <div className="checkin-schedule-editor__reminders-body">
          <div className="checkin-schedule-editor__reminder-row">
            <label htmlFor="reminder-hours-before">
              {t('checkInSchedule.fields.reminderHours', 'Remind before due')}
            </label>
            <div className="checkin-schedule-editor__reminder-right">
              <input
                id="reminder-hours-before"
                type="number"
                className="checkin-schedule-editor__reminder-input"
                min={1}
                max={168}
                value={scheduleValue.reminderHoursBefore ?? 24}
                onChange={(e) =>
                  updateValue({ reminderHoursBefore: Number(e.target.value) || 24 })
                }
                disabled={readOnly}
              />
              <span className="checkin-schedule-editor__reminder-unit">
                {t('checkInSchedule.units.hours', 'hours')}
              </span>
            </div>
          </div>

          <div className="checkin-schedule-editor__reminder-row">
            <label htmlFor="reminder-grace">
              {t('checkInSchedule.fields.gracePeriod', 'Overdue grace')}
            </label>
            <div className="checkin-schedule-editor__reminder-right">
              <input
                id="reminder-grace"
                type="number"
                className="checkin-schedule-editor__reminder-input"
                min={0}
                max={48}
                value={scheduleValue.overdueGracePeriodHours ?? 4}
                onChange={(e) =>
                  updateValue({ overdueGracePeriodHours: Number(e.target.value) || 4 })
                }
                disabled={readOnly}
              />
              <span className="checkin-schedule-editor__reminder-unit">
                {t('checkInSchedule.units.hours', 'hours')}
              </span>
            </div>
          </div>

          <div className="checkin-schedule-editor__reminder-row">
            <label htmlFor="reminder-escalation">
              {t('checkInSchedule.fields.escalationInterval', 'Re-nudge every')}
            </label>
            <div className="checkin-schedule-editor__reminder-right">
              <input
                id="reminder-escalation"
                type="number"
                className="checkin-schedule-editor__reminder-input"
                min={1}
                max={168}
                value={scheduleValue.escalationIntervalHours ?? 24}
                onChange={(e) =>
                  updateValue({ escalationIntervalHours: Number(e.target.value) || 24 })
                }
                disabled={readOnly}
              />
              <span className="checkin-schedule-editor__reminder-unit">
                {t('checkInSchedule.units.hours', 'hours')}
              </span>
            </div>
          </div>

          <div className="checkin-schedule-editor__reminder-row">
            <label htmlFor="reminder-max">
              {t('checkInSchedule.fields.maxEscalations', 'Stop after')}
            </label>
            <div className="checkin-schedule-editor__reminder-right">
              <input
                id="reminder-max"
                type="number"
                className="checkin-schedule-editor__reminder-input"
                min={0}
                max={10}
                value={scheduleValue.maxEscalations ?? 3}
                onChange={(e) =>
                  updateValue({ maxEscalations: Number(e.target.value) || 3 })
                }
                disabled={readOnly}
              />
              <span className="checkin-schedule-editor__reminder-unit">
                {t('checkInSchedule.units.nudges', 'nudges')}
              </span>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* EAINT-886 Board 3 "WHEN A CHECK-IN IS LATE" card. Two independent
          toggles: letVelocityGoStale (default true) gates the existing
          compliance-based velocity decay; degradeStatus (default false)
          auto-clamps the factor's reported status to needs_attention/
          at_risk after 48h/96h overdue. Backend read side lives in
          getVelocityRoute + calculateStatusPoint. */}
      <div className="checkin-schedule-editor__card checkin-schedule-editor__card--late">
        <div className="checkin-schedule-editor__card-title">
          {t('checkInSchedule.sections.whenLate', 'WHEN A CHECK-IN IS LATE')}
        </div>
        <div className="checkin-schedule-editor__late-toggles">
          <div className="checkin-schedule-editor__late-toggle">
            <div className="checkin-schedule-editor__late-toggle-header">
              <label htmlFor="late-let-velocity-go-stale">
                {t('checkInSchedule.lateHandling.velocityStale', 'Let the velocity go stale')}
              </label>
              <SwitchAtom
                inputId="late-let-velocity-go-stale"
                checked={scheduleValue.letVelocityGoStale !== false}
                onChange={(next) => updateValue({ letVelocityGoStale: next })}
                disabled={readOnly}
              />
            </div>
            <small className="checkin-schedule-editor__late-toggle-desc">
              {t(
                'checkInSchedule.lateHandling.velocityStaleDesc',
                'Status still says what was last reported; velocity decays. Conservative default.',
              )}
            </small>
          </div>
          <div className="checkin-schedule-editor__late-toggle">
            <div className="checkin-schedule-editor__late-toggle-header">
              <label htmlFor="late-degrade-status">
                {t('checkInSchedule.lateHandling.degradeStatus', 'Degrade the status')}
              </label>
              <SwitchAtom
                inputId="late-degrade-status"
                checked={!!scheduleValue.degradeStatus}
                onChange={(next) => updateValue({ degradeStatus: next })}
                disabled={readOnly}
              />
            </div>
            <small className="checkin-schedule-editor__late-toggle-desc">
              {t(
                'checkInSchedule.lateHandling.degradeStatusDesc',
                'Not reporting is itself a problem: 48h → needs attention, 96h → at risk.',
              )}
            </small>
          </div>
        </div>
      </div>

      {/* Board 3 Advanced options — collapsed disclosure sitting between
          WHEN A CHECK-IN IS LATE and the backfill warning. Currently houses
          Submission Mode (the second axis from EAINT-747) — hidden by
          default per the mockup, but discoverable. Placeholder for future
          "second intent" advanced knobs. */}
      <details className="checkin-schedule-editor__advanced">
        <summary className="checkin-schedule-editor__advanced-summary">
          {t('checkInSchedule.sections.advancedOptions', 'Advanced options')}
        </summary>
        <div className="checkin-schedule-editor__advanced-body">
          <div className="checkin-schedule-editor__advanced-row">
            <label htmlFor="advanced-submission-mode">
              {t('checkInSchedule.fields.submissionMode', 'Submission mode')}
            </label>
            <select
              id="advanced-submission-mode"
              className="checkin-schedule-editor__inline-select"
              value={scheduleValue.submissionMode ?? 'first_wins'}
              onChange={(e) =>
                updateValue({ submissionMode: e.target.value as SubmissionModeType })
              }
              disabled={readOnly}
            >
              {submissionModeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <small className="checkin-schedule-editor__advanced-hint">
            {scheduleValue.responsibilityMode === 'any_one_check_in'
              ? t(
                  'checkInSchedule.hints.submissionModeAnyOne',
                  'Under "Any one" — picks which single submission the period keeps.',
                )
              : t(
                  'checkInSchedule.hints.submissionModeAllMust',
                  'If the same user submits more than once, picks which of their submissions counts before aggregation.',
                )}
          </small>
        </div>
      </details>

      {/* Board 3 backfill card — red-bordered warning card BELOW REMINDERS
          (was inside Advanced Options). Toggle + max historical picker. */}
      <div className="checkin-schedule-editor__card checkin-schedule-editor__card--backfill">
        <div className="checkin-schedule-editor__card-title checkin-schedule-editor__card-title--danger">
          {t('checkInSchedule.sections.backfill', 'CREATE PERIODS FOR PAST DATES ON DEPLOY')}
          <span className="checkin-schedule-editor__backfill-toggle">
            <SwitchAtom
              inputId="requireAllPeriods"
              checked={!!scheduleValue.requireAllPeriods}
              onChange={(next) => updateValue({ requireAllPeriods: next })}
              disabled={readOnly}
              ariaLabel={t('checkInSchedule.fields.backfillToggle')}
            />
          </span>
        </div>
        <small className="checkin-schedule-editor__hint checkin-schedule-editor__hint--block">
          {t(
            'checkInSchedule.hints.requireAllPeriods',
            'When enabled, past periods from the start date will be created as overdue on deployment. Users will see these as tasks in their task list.'
          )}
        </small>

        {scheduleValue.requireAllPeriods && (() => {
          const days = scheduleValue.maxHistoricalDays ?? 365;
          const unit = scheduleValue.maxHistoricalUnit ?? 'y';
          const displayValue: number = (() => {
            switch (unit) {
              case 'd': return days;
              case 'w': return Math.round(days / 7 * 10) / 10;
              case 'mo': return Math.round(days / 30.44 * 10) / 10;
              case 'y': return Math.round(days / 365.25 * 10) / 10;
              default: return days;
            }
          })();
          const unitToDays = (v: number, u: string): number => {
            switch (u) {
              case 'd': return Math.round(v);
              case 'w': return Math.round(v * 7);
              case 'mo': return Math.round(v * 30.44);
              case 'y': return Math.round(v * 365.25);
              default: return Math.round(v);
            }
          };
          return (
            <div className="checkin-schedule-editor__max-historical-row">
              <label htmlFor="max-historical-input">
                {t('checkInSchedule.fields.maxHistoricalPeriod', 'Limit how far back')}
              </label>
              <div className="checkin-schedule-editor__max-historical-right">
                <input
                  id="max-historical-input"
                  type="number"
                  className="checkin-schedule-editor__reminder-input"
                  min={1}
                  max={5}
                  value={displayValue || 1}
                  onChange={(e) => {
                    const next = Number(e.target.value) || 1;
                    updateValue({
                      maxHistoricalDays: unitToDays(next, unit),
                      maxHistoricalUnit: unit as MaxHistoricalUnit,
                    });
                  }}
                  disabled={readOnly}
                />
                <select
                  className="checkin-schedule-editor__inline-select"
                  value={unit}
                  onChange={(e) => {
                    const nextUnit = e.target.value as MaxHistoricalUnit;
                    updateValue({
                      maxHistoricalDays: unitToDays(displayValue || 1, nextUnit),
                      maxHistoricalUnit: nextUnit,
                    });
                  }}
                  disabled={readOnly}
                  aria-label={t('checkInSchedule.fields.maxHistoricalUnit', 'Unit')}
                >
                  <option value="d">{t('checkInSchedule.units.days', 'days')}</option>
                  <option value="w">{t('checkInSchedule.units.weeks', 'weeks')}</option>
                  <option value="mo">{t('checkInSchedule.units.months', 'months')}</option>
                  <option value="y">{t('checkInSchedule.units.years', 'years')}</option>
                </select>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default CheckInScheduleEditorMolecule;
